import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type { BlogFormValues, BlogListApiResponse, BlogPayload } from "interface/blog";
import type { StatusInterface } from "interface/common";

export function blogFormToApiBody(values: BlogFormValues): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: values.name.trim(),
    description: values.description,
    meta_title: values.metaTitle.trim(),
    meta_description: values.metaDescription.trim(),
    meta_keywords: values.metaKeywords.trim(),
  };
  if (values.id) {
    body.id = values.id;
  }
  return body;
}

export const blogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlogs: builder.query<BlogListApiResponse, { [key: string]: string | number }>({
      query: (params) => ({
        url: API.GET_BLOG,
        method: "GET",
        params,
      }),
      providesTags: ["Blog"],
    }),
    createBlog: builder.mutation<BlogListApiResponse, BlogPayload>({
      query: ({ params }) => ({
        url: API.GET_BLOG,
        method: "POST",
        body: blogFormToApiBody(params),
      }),
      invalidatesTags: ["Blog"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create blog:", error);
        }
      },
    }),
    updateBlog: builder.mutation<BlogListApiResponse, BlogPayload>({
      query: ({ params }) => ({
        url: API.GET_BLOG,
        method: "PUT",
        body: blogFormToApiBody(params),
      }),
      invalidatesTags: ["Blog"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to update blog:", error);
        }
      },
    }),
    deleteBlog: builder.mutation<BlogListApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_BLOG}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Blog"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          console.error("Failed to delete blog:", error);
        }
      },
    }),
    updateBlogStatus: builder.mutation<BlogListApiResponse, StatusInterface>({
          query: ({ id, ...body }) => ({
            url: `/${API.GET_BLOG}/status/${id}`,
            method: "PUT",
            body,
          }),
          invalidatesTags: ["Blog"],
          async onQueryStarted(_arg, { queryFulfilled }) {
            try {
              await queryFulfilled;
            } catch (error) {
              console.error("Failed to update blog status:", error);
            }
          },
        }),
    bulkUpdateBlogStatus: builder.mutation<
      BlogListApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_BLOG}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["Blog"],
    }),
    bulkDeleteBlogs: builder.mutation<BlogListApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_BLOG}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["Blog"],
    }),
  }),
});

export const {
  useGetBlogsQuery,
  useCreateBlogMutation,
  useUpdateBlogMutation,
  useDeleteBlogMutation,
  useUpdateBlogStatusMutation,
  useBulkUpdateBlogStatusMutation,
  useBulkDeleteBlogsMutation,
} = blogApi;
